/**
 * PricingDisplay - Shows tiered pricing based on distance
 */

import type { EventPricingResponse } from '../../api/events';
import { formatPrice } from '../../api/events';

interface PricingDisplayProps {
  pricing: EventPricingResponse;
  selectedClassId?: string;
  onSelectClass?: (classId: string) => void;
  selectedDate?: string;
}

/**
 * Map pricing tier to display info
 */
const TIER_INFO: Record<string, { label: string; color: string; description: string }> = {
  standard: {
    label: 'Standard',
    color: 'bg-success-100 text-success-700',
    description: 'No travel surcharge',
  },
  tier_1: {
    label: 'Tier 1',
    color: 'bg-primary-100 text-primary-700',
    description: '+$25 travel surcharge',
  },
  tier_2: {
    label: 'Tier 2',
    color: 'bg-warning-100 text-warning-700',
    description: '+$50 travel surcharge',
  },
  dual_option: {
    label: 'Extended',
    color: 'bg-primary-100 text-primary-700',
    description: 'Multiple booking options available',
  },
  hosted: {
    label: 'Remote',
    color: 'bg-warm-100 text-warm-700',
    description: 'Inquiry required',
  },
};

const DEFAULT_TIER_INFO = { label: 'Standard', color: 'bg-success-100 text-success-700', description: 'No travel surcharge' };

export function PricingDisplay({ pricing, selectedClassId, onSelectClass, selectedDate }: PricingDisplayProps) {
  const tierInfo = TIER_INFO[pricing.pricing_tier.tier] ?? DEFAULT_TIER_INFO;

  return (
    <div className="space-y-6">
      {/* Distance & Tier Badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${tierInfo.color}`}>
            {tierInfo.label}
          </span>
          <span className="text-warm-500 text-sm">
            {Math.round(pricing.distance_miles)} miles from your studio
          </span>
        </div>
        {pricing.using_straight_line && (
          <span className="text-warm-400 text-xs">
            *Approximate distance
          </span>
        )}
      </div>

      {/* Tier Description */}
      <p className="text-warm-600">{tierInfo.description}</p>

      {/* Class Options */}
      <div className="space-y-3">
        <h4 className="font-semibold text-warm-900">Class Options</h4>
        {pricing.class_options.map((option) => (
          <ClassOptionCard
            key={option.id}
            option={option}
            isSelected={selectedClassId === option.id}
            onSelect={onSelectClass ? () => onSelectClass(option.id) : undefined}
          />
        ))}
      </div>

      {/* Pricing Note */}
      <div className="bg-warm-50 rounded-lg p-4 text-sm text-warm-600">
        <p className="flex items-start gap-2">
          <svg
            className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>
            {pricing.pricing_tier.surcharge_cents > 0
              ? 'Prices shown include travel surcharge. '
              : ''}
            {selectedDate && new Date(selectedDate) <= new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)
              ? 'Full payment is required at booking — class is within 15 days.'
              : 'A $100 deposit is required at booking, with the balance due 15 days before your class.'}
          </span>
        </p>
      </div>
    </div>
  );
}

/**
 * Individual class option card
 */
interface ClassOptionCardProps {
  option: EventPricingResponse['class_options'][number];
  isSelected?: boolean;
  onSelect?: () => void;
}

function ClassOptionCard({ option, isSelected, onSelect }: ClassOptionCardProps) {
  const hasInteraction = !!onSelect;

  const content = (
    <>
      <div className="flex-1">
        <h5 className="font-medium text-warm-900">{option.name}</h5>
        <p className="text-sm text-warm-500">
          {option.duration_minutes} minutes · Up to {option.max_students} students
        </p>
      </div>
      <div className="text-right">
        <div className="text-xl font-bold text-warm-900">
          {formatPrice(option.total_price_cents)}
        </div>
        {option.travel_surcharge_cents > 0 && (
          <div className="text-xs text-warm-500">
            Includes {formatPrice(option.travel_surcharge_cents)} travel
          </div>
        )}
      </div>
    </>
  );

  if (hasInteraction) {
    return (
      <button
        type="button"
        onClick={onSelect}
        className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
          isSelected
            ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
            : 'border-warm-200 hover:border-primary-300 hover:bg-warm-50'
        }`}
      >
        {content}
        {isSelected && (
          <div className="ml-4">
            <svg
              className="h-6 w-6 text-primary-500"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
        )}
      </button>
    );
  }

  return (
    <div className="flex items-center justify-between p-4 rounded-lg border border-warm-200 bg-white">
      {content}
    </div>
  );
}

export default PricingDisplay;
